import styled from "@emotion/styled";
import { CategorySectionProps, EventPlatformEntry, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import { PlaylistsContainer } from "./PlaylistsContainer";
import { ConfigProvider, useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { KeywordSearchInput } from "./KeywordSearchInput";
import { FiltersValues } from "@kaltura/mediaspace-shared-search-with-filter-tags";

export interface EventPlatformPlaylist {
    entries: EventPlatformEntry[];
    id: string;
    name: string;
    titleAreaButtons?: KmsTypeComponent[];
}

/**
 * expected server response when loading more playlists
 */
export type PlaylistsResponse = {
    /**
     * error description in case of error
     */
    error?: string;

    /**
     * playlists data
     */
    playlists: EventPlatformPlaylist[];

    /**
     * are there more playlists to load
     */
    morePlaylists: boolean;
};

export interface PlaylistCarouselsProps extends CategorySectionProps<EventPlatformPlaylist[]> {
    hasMorePlaylists: boolean;
    hideFilters?: boolean;
    filterByKeyword?: string;
    filterByTags?: FiltersValues;
    onNoResults?: () => void;
}

const StyledSearchLine = styled("div")(({ theme }) => ({
    width: "100%",
    marginBottom: theme.spacing(2),
}));

/**
 * Channel page playlists section
 */
export function PlaylistCarousels(props: PlaylistCarouselsProps) {
    const {
        content,
        categoryId = undefined,
        hasMorePlaylists,
        hideFilters = true,
        filterByKeyword,
        filterByTags,
        onNoResults,
    } = props;

    /**
     * search keyword
     */
    const [searchKeyword, setSearchKeyword] = useState<string>("");

    const handleSearchTermChange = (value: string) => {
        setSearchKeyword(value);
    };

    const config = useConfig();
    const playlistsSearchConfig = {
        ...config,
        analytics: { ...config.analytics, pageType: "Channel - Playlists tab" },
    };

    return (
        <ConfigProvider context={playlistsSearchConfig}>
            <div className={"kms-ds-channel-page-section-playlists"}>
                {!hideFilters && (
                    <StyledSearchLine>
                        <KeywordSearchInput
                            ariaLabel={translate("Search Playlists")}
                            placeholder={translate("Search in Playlists")}
                            onSubmit={handleSearchTermChange}
                        />
                    </StyledSearchLine>
                )}
                <PlaylistsContainer
                    categoryId={categoryId}
                    hasMorePlaylists={hasMorePlaylists}
                    initialPlaylists={content}
                    searchKeyword={searchKeyword || filterByKeyword}
                    showNoResults={!hideFilters}
                    searchByTags={filterByTags}
                    onNoResults={onNoResults}
                />
            </div>
        </ConfigProvider>
    );
}

export default PlaylistCarousels;
